import { useState, useEffect, useRef } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Comission from 'services/Comission'
import Dashboard from 'services/Dashboard'
import Worksheet from 'services/Worksheet'
import General from 'services/General'
import Select from 'components/Inputs/Select'
import DateInput from 'components/Inputs/DateInput'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AddProfessionalCreditModal from 'pages/Modals/AddProfessionalCreditModal'
import AddProfessionalDebtModal from 'pages/Modals/AddProfessionalDebtModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaPlus, FaFileExcel, FaChevronRight } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  ListContainer,
  DayContainer,
  FilterContainer,
  Row,
  LabelRow,
  SubTitle,
  MoneyTextMask
} from '../styles'

export default function Comissions () {
  const startDate = localStorage.getItem ('financial_start_date')
  const endDate = localStorage.getItem ('financial_end_date')
  const [loading, setLoading] = useState (true)
  const [business, setBusiness] = useState ({})
  const [professionals, setProfessionals] = useState ([])
  const [categories, setCategories] = useState ([])
  const [category, setCategory] = useState (null)
  const [earningsByPeriod, setEarningsByPeriod] = useState ([])
  const [earningsByDay, setEarningsByDay] = useState ([])
  const [sortedEarnings, setSortedEarnings] = useState ([])
  const [bonuses, setBonuses] = useState ([])
  const [discounts, setDiscounts] = useState ([])
  const [bonusTotal, setBonusTotal] = useState (0)
  const [discountTotal, setDiscountTotal] = useState (0)
  const [showAddCreditModal, setShowAddCreditModal] = useState (false)
  const [showAddDebtModal, setShowAddDebtModal] = useState (false)
  const [selectedProfessional, setSelectedProfessional] = useState (
    JSON.parse (localStorage.getItem ('financial_selected_professional')) ?? {
      label: 'Todos os profissionais', value: 'all_professionals'
    }
  )
  const [earningType, setEarningType] = useState (
    JSON.parse (localStorage.getItem ('financial_earning_type')) ?? {
      label: 'Todos os rendimentos', value: 'all_earnings'
    }
  )
  const [dateRange, setDateRange] = useState (startDate && endDate
    ? [startDate, endDate]
    : [DateTime.now ().startOf ('month').toISODate (), DateTime.now ().toISODate ()]
  )

  const latestRequest = useRef(0)

  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const canViewTotalEarnings = AuthService.hasPermission ('financial.total_earnings')
  const canViewBusinessEarnings = AuthService.hasPermission ('financial.business_earnings')
  const isAllProfessionals = selectedProfessional.value === 'all_professionals'
  const professionalLabel = isAllProfessionals ? 'dos profissionais' : 'do profissional'

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  useEffect (() => {
    getEarningsByPeriod ()
  }, [dateRange, selectedProfessional, earningType, category])

  useEffect (() => {
    getSortedEarnings ()
  }, [earningsByPeriod])

  const getData = async () => {
    try {
      const professionalFilters = `access_level=service&deleted=false&date=${dateRange[1]}`

      const [{ data: business }, { data: categories }, { data: professionals }] = await Promise.all ([
        cutileiApi.get (`/businesses/${businessId}`),
        cutileiApi.get ('/service_categories', requestConfig),
        cutileiApi.get (`/businesses/${businessId}/professionals?${professionalFilters}`, requestConfig)
      ])

      setBusiness (business)
      setProfessionals (professionals)
      setCategories (categories.map (category => ({
        label: category.name,
        value: category.id
      })))
    } catch (error) {
      logApiError (error)
    }
  }

  const showReport = ({ earnings = [], bonuses = [], discounts = [] } = {}) => {
    setBonuses(bonuses)
    setDiscounts(discounts)
    setBonusTotal(bonuses.reduce((total, bonus) => total + bonus.value, 0))
    setDiscountTotal(discounts.reduce((total, discount) => total + discount.value * -1, 0))
    setEarningsByPeriod(earnings)
  }

  const getEarningsByPeriod = async () => {
    const startDate = dateRange ? dateRange[0] : null
    const endDate = dateRange ? dateRange[1] : null
    const requestId = ++latestRequest.current
    const isStale = () => requestId !== latestRequest.current

    let params = `?business_id=${businessId}&start_date=${startDate}&end_date=${endDate}`
               + `&type=${earningType.value}`
    if (category) params += `&service_category_id=${category.value}`
    if (!isAllProfessionals) params += `&professional_id=${selectedProfessional.value}`

    try {
      setLoading(true)

      // Uma chamada só, com o que o relatório lê. O que o filtro não pede volta como lista vazia:
      // bônus e descontos só vêm com um profissional escolhido e todos os rendimentos.
      const { data } = await cutileiApi.get(`/earnings_report${params}`)

      if (isStale()) return

      const earnings = [
        ...data.schedules.map(schedule => ({...schedule, type: 'cutilei'})),
        ...data.bills.map(bill => ({...bill, type: 'bill'})),
        ...data.credits.map(credit => ({...credit, type: 'bonus'})),
        ...data.debts.map(debt => ({...debt, value: debt.value * -1, type: 'discount'}))
      ].sort(orderEarningsByDate)

      showReport({ earnings, bonuses: data.credits, discounts: data.debts })
    } catch (error) {
      if (isStale()) return

      // Sem isso a tela seguiria mostrando os números do filtro anterior como se fossem do novo.
      showReport()
      logApiError(error)
    } finally {
      if (!isStale()) setLoading(false)
    }
  }

  const getSortedEarnings = () => {
    const earningsByDay = General.groupBy (earningsByPeriod, 'date')
    const totalEarnings = Comission.getTotalEarnings (earningsByPeriod, earningType, category)
    const businessEarnings = Comission.getBusinessEarnings (earningsByPeriod, earningType, category)
    const professionalEarnings = Comission.getProfessionalEarnings (earningsByPeriod, earningType, category)

    const sortedEarnings = [{
      day: 'TOTAL DO PERÍODO',
      totalEarnings,
      professionalEarnings,
      ...bonuses.length > 0 && {bonusTotal},
      ...discounts.length > 0 && {discountTotal},
      professionalTotalEarnings: professionalEarnings + bonusTotal + discountTotal,
      businessEarnings
    }].concat (earningsByDay.map (earnings => {
      const day = DateTime.fromISO (earnings[0].date).toFormat ('dd/MM/yyyy')
      const dayBonuses = earnings.filter (e => e.type === 'bonus')
      const dayDiscounts = earnings.filter (e => e.type === 'discount')
      const dayBonusTotal = dayBonuses.reduce ((total, bonus) => total + bonus.value, 0)
      const dayDiscountTotal = dayDiscounts.reduce ((total, discount) => total + discount.value, 0)
      const totalDayEarnings = Comission.getTotalEarnings (earnings, earningType, category)
      const professionalDayEarnings = Comission.getProfessionalEarnings (earnings, earningType, category)
      const professionalTotalDayEarnings = professionalDayEarnings + dayBonusTotal + dayDiscountTotal
      const businessDayEarnings = Comission.getBusinessEarnings (earnings, earningType, category)

      return ({
        day,
        totalEarnings: totalDayEarnings,
        professionalEarnings: professionalDayEarnings,
        ...bonuses.length > 0 && {bonusTotal: dayBonusTotal},
        ...discounts.length > 0 && {discountTotal: dayDiscountTotal},
        professionalTotalEarnings: professionalTotalDayEarnings,
        businessEarnings: businessDayEarnings
      })
    }))

    setEarningsByDay (earningsByDay)
    setSortedEarnings (sortedEarnings)
  }

  const handleExportSheet = () => Worksheet.createTotalEarningsSheet ({
    dateRange, selectedProfessional, sortedEarnings, bonuses, discounts
  })

  const orderEarningsByDate = (a, b) => (
    DateTime.fromISO (b.date).toMillis () - DateTime.fromISO (a.date).toMillis ()
  )

  const toggleAddCreditModal = () => setShowAddCreditModal (!showAddCreditModal)
  const toggleAddDebtModal = () => setShowAddDebtModal (!showAddDebtModal)

  return (
    <ListContainer>
      <FilterContainer>
        <DateInput
          value={dateRange}
          selectRange={true}
          onChange={dateRange => {
            setDateRange (dateRange)
            localStorage.setItem ('financial_start_date', dateRange[0])
            localStorage.setItem ('financial_end_date', dateRange[1])
          }}
        />
        <Select
          name='scheduleType'
          placeholder='Tipo de agendamento...'
          value={earningType}
          options={Dashboard.TYPE_FILTERS}
          onChange={(_, option) => {
            setEarningType (option)
            localStorage.setItem ('financial_earning_type', JSON.stringify (option))
          }}
        />
        <Select
          name='professional'
          placeholder='Profissional...'
          value={selectedProfessional}
          options={[
            {label: 'Todos os profissionais', value: 'all_professionals'},
            ...professionals.map (professional => ({
              label: professional.nickname,
              value: professional.id
            }))
          ]}
          onChange={(_, option) => {
            setSelectedProfessional (option)
            localStorage.setItem ('financial_selected_professional', JSON.stringify (option))
          }}
        />
        <Select
          name='category'
          placeholder='Categoria de serviço...'
          value={category}
          options={categories}
          isClearable={true}
          onChange={(_, option) => setCategory (option)}
        />
      </FilterContainer>
      {loading ? <Loading style={{marginTop: 20}}/> : (
        <>
          <Row>
            {AuthService.hasPermission ('financial.comissions', 'edit') && (
              <>
                <SecondaryButton
                  label='Bônus'
                  leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
                  onClick={toggleAddCreditModal}
                  width={140}
                />
                <SecondaryButton
                  label='Desconto'
                  leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
                  onClick={toggleAddDebtModal}
                  width={140}
                />
              </>
            )}
            <SecondaryButton
              label='Exportar'
              leftIcon={<FaFileExcel size={14} color='#FFC100' style={{marginBottom: 1}}/>}
              onClick={handleExportSheet}
              width={140}
            />
          </Row>
          <ListContainer>
            <LabelRow
              bonuses={bonuses}
              discounts={discounts}
              showTotalEarnings={canViewTotalEarnings}
              showBusinessEarnings={canViewBusinessEarnings}
            >
              <SubTitle>Dia</SubTitle>
              {canViewTotalEarnings && <SubTitle>Total bruto</SubTitle>}
              <SubTitle>Comissões</SubTitle>
              {bonuses.length > 0 && <SubTitle>Bônus</SubTitle>}
              {discounts.length > 0 && <SubTitle>Descontos</SubTitle>}
              <SubTitle>Total {professionalLabel}</SubTitle>
              {canViewBusinessEarnings && <SubTitle>Total do salão</SubTitle>}
            </LabelRow>
            {sortedEarnings.map ((earning, index) => {
              const {
                day, totalEarnings, businessEarnings, professionalEarnings,
                bonusTotal, discountTotal, professionalTotalEarnings
              } = earning

              return (
                <DayContainer
                  key={index}
                  bonuses={bonuses}
                  discounts={discounts}
                  showTotalEarnings={canViewTotalEarnings}
                  showBusinessEarnings={canViewBusinessEarnings}
                  to={index === 0
                    ? { pathname: '/business/financial/total' }
                    : { pathname: '/business/financial/daily', search: `?day=${day}` }
                  }
                  state={index === 0 ? {
                    business,
                    dateRange,
                    earningType,
                    category,
                    professionals,
                    selectedProfessional,
                    totalEarnings,
                    businessEarnings,
                    professionalEarnings,
                    earningsByPeriod
                  } : {
                    day,
                    dayEarnings: earningsByDay[index - 1],
                    earningType,
                    category,
                    selectedProfessional
                  }}
                >
                  <SubTitle>{earning.day}</SubTitle>
                  {canViewTotalEarnings && <MoneyTextMask value={totalEarnings}/>}
                  <MoneyTextMask value={professionalEarnings}/>
                  {bonuses.length > 0 ? (
                    bonusTotal !== 0 ? <MoneyTextMask value={bonusTotal ?? 0}/> : <b>-</b>
                  ) : null}
                  {discounts.length > 0 ? (
                    discountTotal !== 0 ? <MoneyTextMask value={discountTotal ?? 0}/> : <b>-</b>
                  ) : null}
                  <MoneyTextMask value={professionalTotalEarnings}/>
                  {canViewBusinessEarnings && <MoneyTextMask value={businessEarnings}/>}
                  <FaChevronRight size={15} color='#252525'/>
                </DayContainer>
              )
            })}
          </ListContainer>
        </>
      )}
      <AddProfessionalCreditModal
        visible={showAddCreditModal}
        professionals={professionals}
        onConfirm={getEarningsByPeriod}
        onClose={toggleAddCreditModal}
      />
      <AddProfessionalDebtModal
        visible={showAddDebtModal}
        professionals={professionals}
        onConfirm={getEarningsByPeriod}
        onClose={toggleAddDebtModal}
      />
    </ListContainer>
  )
}
